import { ClickAwayListener, IconButton } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import type { Control, FieldErrors, UseFormSetValue } from "react-hook-form";

import FormTextFieldDark from "@/components/UI/FormTextFieldDark/FormTextFieldDark";
import { ELEMENT_LINK_DESTINATION } from "@/constants";
import { ArrowDownIcon } from "@/icons";

import type { LinkFormData } from "../LinkForm/types.ts";

const useStyles = makeStyles({
  container: {
    position: "relative",
  },
  list: {
    padding: "16px",
    border: "1px solid #323232",
    background: "#000",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.25), 0px 1px 2px 1px rgba(0, 0, 0, 0.25)",
    margin: "0 auto",
    width: "calc(100% - 32px)",
    listStyle: "none",
    position: "absolute",
    top: "56px",
    left: "16px",
    zIndex: "999",
    maxHeight: "216px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#161616",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
  },
  listItem: {
    padding: "4px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    lineHeight: "20px",
    overflow: "hidden",
    width: "100%",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    textAlign: "left",
    marginBottom: "5px",
    "&:hover": {
      background: "#300013",
    },
  },
});

type MediaItem = {
  link: string;
  xFrameOptions?: string;
  id: number;
  isEmbeddable: boolean;
};

type ElementsSelectFieldProps = {
  mediaItems: MediaItem[];
  formValues: Partial<LinkFormData>;
  control: Control<LinkFormData>;
  setValue: UseFormSetValue<LinkFormData>;
  errors: FieldErrors<LinkFormData>;
};

export default function ElementsSelectField(props: ElementsSelectFieldProps) {
  const { mediaItems, formValues, control, setValue } = props;
  const classes = useStyles();
  const { url } = formValues;

  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<MediaItem[]>([]);

  useEffect(() => {
    if (!url || url.length < 2) {
      setOptions(mediaItems);
    } else {
      setOptions(
        mediaItems.filter((item) => item.link.toLowerCase().indexOf(formValues.url?.toLowerCase() || "") > -1),
      );
    }
  }, [mediaItems.length, formValues.url]);

  return (
    <div className={classes.container} data-testid="link-autocomplete-field-container">
      <FormTextFieldDark
        name="url"
        control={control}
        label="Enter URL or select from assets"
        variant="standard"
        fullWidth
        autoComplete="off"
        onFocus={() => {
          setIsOpen(true);
        }}
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => setIsOpen(true)}>
              <ArrowDownIcon htmlColor="#757575" />
            </IconButton>
          ),
        }}
        data-analytics="link-field-url"
      />
      {isOpen && !!options.length && (
        <ClickAwayListener
          onClickAway={(e: MouseEvent | TouchEvent) => {
            if ((e.target as HTMLInputElement)?.name !== "url") {
              setIsOpen(false);
            }
          }}
        >
          <div className={classes.list}>
            {options.map((option) => (
              <button
                type="button"
                className={classes.listItem}
                key={`list-item-${option.id}`}
                onClick={async () => {
                  setValue("url", option.link, { shouldDirty: true, shouldValidate: true });
                  setValue("linkedId", option.id, { shouldDirty: true, shouldValidate: true });
                  setIsOpen(false);
                  setValue("xFrameOptions", option.xFrameOptions);
                  setValue(
                    "linkDestination",
                    option.isEmbeddable ? ELEMENT_LINK_DESTINATION.MODAL : ELEMENT_LINK_DESTINATION.NEW_TAB,
                  );
                  setValue("isEmbeddable", option.isEmbeddable);
                }}
              >
                {option.link}
              </button>
            ))}
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
}
